import styled, { css } from 'styled-components';

import { fadeIn } from '../../styles/shared.style';
import { skeletonShimmer } from '../Resale/sharedStyles';

export type ResaleSoldOutVariant = 'waitlist' | 'listing' | 'explore';

// Figma text colors with no matching theme token (text.primary/secondary are different shades).
const TITLE_COLOR = '#171717';
const DESCRIPTION_COLOR = '#404040';

const ICON_SIZE: Record<ResaleSoldOutVariant, { desktop: number; mobile: number }> = {
  waitlist : { desktop : 120, mobile : 100 },
  listing : { desktop : 161, mobile : 120 },
  explore : { desktop : 161, mobile : 120 },
};

const TEXTS_GAP: Record<ResaleSoldOutVariant, string> = {
  waitlist : '8px',
  listing : '16px',
  explore : '16px',
};

const DESCRIPTION_MAX_WIDTH: Record<ResaleSoldOutVariant, string> = {
  waitlist : '723px',
  listing : '758px',
  explore : '758px',
};

// The state fills the tickets page's free space via flex (flex: 1 inside the
// pages/tickets Container, which grows to the bottom of the Layout content area) and
// centers its content there. No viewport-based min-height: the
// "100vh - fixed header height" math didn't match the real header and overflowed the
// screen on desktop, causing empty scrolling. On soccer events the club member block
// (IdentifyMember) sits above in the same column, and flex already accounts for its height.
// Also applies to EventNotFoundState, which reuses this Container.
export const Container = styled.section`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 0 24px;
  text-align: center;
  animation: ${fadeIn} 0.3s forwards;

  /* On mobile the block keeps its natural height, right below the header (not centered
     in the free space); bottom spacing comes from the tickets page padding. */
  ${({ theme }) => theme.media.mobile} {
    flex: none;
    padding: 32px 24px 0;
  }
`;

export const Icon = styled.img<{ $variant: ResaleSoldOutVariant }>`
  ${({ $variant }) => css`
    width: ${ICON_SIZE[$variant].desktop}px;
    height: ${ICON_SIZE[$variant].desktop}px;
  `}

  ${({ theme }) => theme.media.mobile} {
    ${({ $variant }) => css`
      width: ${ICON_SIZE[$variant].mobile}px;
      height: ${ICON_SIZE[$variant].mobile}px;
    `}
  }
`;

export const Content = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

export const Texts = styled.div<{ $variant: ResaleSoldOutVariant }>`
  display: flex;
  width: 100%;
  max-width: ${({ $variant }) => DESCRIPTION_MAX_WIDTH[$variant]};
  flex-direction: column;
  align-items: center;
  gap: ${({ $variant }) => TEXTS_GAP[$variant]};
`;

export const Title = styled.h2`
  margin: 0;
  color: ${TITLE_COLOR};
  font-family: ${({ theme }) => theme.font.family};
  font-size: 36px;
  font-weight: 700;
  line-height: 44px;
  letter-spacing: -0.02em;
  text-align: center;

  ${({ theme }) => theme.media.mobile} {
    font-size: 30px;
    line-height: 38px;
  }
`;

export const Description = styled.p`
  margin: 0;
  color: ${DESCRIPTION_COLOR};
  font-family: ${({ theme }) => theme.font.family};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
`;

// Primary button style for the sold-out states; shared with CtaLink (anchor),
// used when the CTA leaves the checkout.
export const ctaButtonStyles = css`
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.button.surface.primary};
  box-shadow:
    0 1px 2px 0 rgba(0, 0, 0, 0.05),
    inset 0 -2px 0 0 rgba(0, 0, 0, 0.05),
    inset 0 0 0 1px rgba(0, 0, 0, 0.18);
  color: ${({ theme }) => theme.colors.button.text.primary};
  font-family: ${({ theme }) => theme.font.family};
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.button.hover.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.button.surface.primary};
    outline-offset: 2px;
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.button.surface.disabled};
    color: ${({ theme }) => theme.colors.button.text.disabled};
    cursor: not-allowed;
  }

  ${({ theme }) => theme.media.mobile} {
    width: 100%;
  }
`;

export const CtaButton = styled.button`
  ${ctaButtonStyles}
`;

// CTA that leaves the checkout (explore variant and EventNotFoundState): an <a> by
// definition (doesn't rely on `as` at the call site), styled as the primary button.
export const CtaLink = styled.a`
  ${ctaButtonStyles}
  text-decoration: none;

  /* Overrides globalStyles' a:hover color (same as the hover background, so the text vanished). */
  &:hover,
  &:focus,
  &:visited {
    color: ${({ theme }) => theme.colors.button.text.primary};
  }
`;

// Screen-reader-only text (e.g. the placeholder's role="status" region message).
export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// Placeholder shown while the resale check is in flight ("pending" scenario on the tickets
// page). Uses the same Container and dimensions as the final state (listing/explore icon,
// title line, description and CTA) so the area doesn't change height on swap.
const PLACEHOLDER_ICON_SIZE = ICON_SIZE.listing;

export const SkeletonIcon = styled.span`
  display: block;
  width: ${PLACEHOLDER_ICON_SIZE.desktop}px;
  height: ${PLACEHOLDER_ICON_SIZE.desktop}px;
  border-radius: 50%;
  ${skeletonShimmer}

  ${({ theme }) => theme.media.mobile} {
    width: ${PLACEHOLDER_ICON_SIZE.mobile}px;
    height: ${PLACEHOLDER_ICON_SIZE.mobile}px;
  }
`;

export const SkeletonLine = styled.span<{ $height: number; $mobileHeight?: number; $width: string }>`
  display: block;
  width: 100%;
  max-width: ${({ $width }) => $width};
  height: ${({ $height }) => $height}px;
  border-radius: 8px;
  ${skeletonShimmer}

  ${({ theme }) => theme.media.mobile} {
    height: ${({ $height, $mobileHeight }) => $mobileHeight ?? $height}px;
  }
`;
